import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { DrawerHeader } from "../../../../components/drawer/drawer-header";
import { Drawer } from "../../../../components/drawer/drawer";
import { Form, Spin, Modal } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import {
  FormHeaderTextarea,
  FormImage,
  FormSelect,
  FormSwitch,
  FormInput,
  FormFieldName,
} from "../../../../components/form/form";
import { Editor } from "../../../../components/editor/editor";
import TagsSelect from "../../../../components/tags-select/tags-select";
import { useSelector } from "react-redux";
import { selectUser } from "../../../../store/slices/user";
import ApiArticles from "../../../../api/articles";
import { useProjectNavigate } from "../../../../common/utils/useProjectNavigate";
import ApiUserUploads from "../../../../api/user-uploads";
import debounce from "../../../../common/utils/debounce";
import UserActivityStatus from "../../../../components/user-activity-status/user-activity-status";
import DiscardModal from "../../../../components/modals/discard/discard-modal.js";
import ChangesDiffModalComponent from "../../../../components/modals/change-logs/changes-diff-modal.js";
import AssociationsBlock from "../../../../components/associations/associations-block";
import ScenarioSimulatorQr from "../../../../components/scenario-simulator/scenario-simulator-qr";
import useNotification from "../../../../components/notification/notification";

// ---------- helpers ----------
function normalizeMediaBlocks(input) {
  if (!input) return input;

  let s = String(input);

  // self-closing <video ... /> -> <video ...></video>
  s = s.replace(/<video([^>]*?)\/>/gi, "<video$1></video>");

  // ensure <img ...> is separated by blank lines from text
  s = s.replace(/(\S)(<img\b)/gi, "$1\n\n$2");
  s = s.replace(/(<img\b[^>]*>)(\S)/gi, "$1\n\n$2");

  // ensure <video ...>...</video> is separated
  s = s.replace(/(\S)(<video\b)/gi, "$1\n\n$2");
  s = s.replace(/(<\/video>)(\S)/gi, "$1\n\n$2");

  // keep it tidy
  s = s.replace(/\n{3,}/g, "\n\n");

  return s;
}

const articleSpinner = (
  <div
    style={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
    }}
  >
    <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
  </div>
);

const importSpinnerOverlay = (
  <div
    style={{
      position: "absolute",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "rgba(255, 255, 255, 0.66)",
      backdropFilter: "blur(1px)",
      zIndex: 20,
    }}
  >
    <div style={{ textAlign: "center" }}>
      <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
      <div style={{ marginTop: 12, fontSize: 14, opacity: 0.8 }}>
        Importing document...
      </div>
    </div>
  </div>
);

export const ArticleModal = ({
  form,
  data,
  onClose,
  onSave, // unused but keep
  tags,
  categories,
  articleId,
  updateHasImage,
  updateIsUpdatedImage,
  isLoading,
  authors = [],
  mode,
  onDiscard,
  onPublish,
  onSaveSilent,
  remove,
  toggleActive,
  duplicate,
}) => {
  const { openNotification } = useNotification();
  const [isImporting, setIsImporting] = useState(false);

  // -------- signed url refresh --------
  const fetchUpdatedMediaUrls = useCallback(async (content) => {
    if (!content) return content;

    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");

    const uploadIds = new Set();

    doc.querySelectorAll("img").forEach((img) => {
      const alt = img.getAttribute("alt") || "";
      const match = alt.match(/^image-([a-f0-9-]+)$/i);
      if (match) {
        uploadIds.add(match[1]);
      }
    });

    doc.querySelectorAll("video[data-upload-id]").forEach((video) => {
      const uploadId = video.getAttribute("data-upload-id");
      if (uploadId) uploadIds.add(uploadId);
    });

    const markdownImageRegex = /!\[(image-[a-f0-9-]+)\]\((.*?)\)/gi;
    let mdMatch;
    while ((mdMatch = markdownImageRegex.exec(content)) !== null) {
      uploadIds.add(mdMatch[1].replace("image-", ""));
    }

    if (uploadIds.size === 0) return content;

    const response = await ApiUserUploads.getSignedUrls({
      imageIds: [...uploadIds],
    });

    const urlMap = response?.data || {};

    doc.querySelectorAll("img").forEach((img) => {
      const alt = img.getAttribute("alt") || "";
      const match = alt.match(/^image-([a-f0-9-]+)$/i);
      if (!match) return;

      const imageId = match[1];
      const newUrl = urlMap[imageId];
      if (newUrl) {
        img.setAttribute("src", newUrl);
      }
    });

    doc.querySelectorAll("video[data-upload-id]").forEach((video) => {
      const uploadId = video.getAttribute("data-upload-id");
      const newUrl = urlMap[uploadId];
      if (newUrl) {
        video.setAttribute("src", newUrl);
      }
    });

    let updated = doc.body.innerHTML.replace(
      markdownImageRegex,
      (m, alt) => {
        const imageId = alt.replace("image-", "");
        const newUrl = urlMap[imageId];
        return newUrl ? `![${alt}](${newUrl})` : m;
      }
    );

    return normalizeMediaBlocks(updated);
  }, []);

  const navigate = useProjectNavigate();
  const urlPart = "/content/articles";

  const currentUser = useSelector(selectUser);
  
  const [editMode, setEditMode] = useState(false);

  const [fetchedArticle, setFetchedArticle] = useState(null);
  const [isLoadingArticle, setIsLoadingArticle] = useState(false);

  // ---- local editable bits (як було) ----
  const [editorContent, setEditorContent] = useState(null); // null = not ready yet
  const [articleImage, setArticleImage] = useState(undefined);
  const [isExternal, setIsExternal] = useState(data?.external ?? false);

  // this increments ONLY when we want to force-remount Editor (external update)
  const [contentVersion, setContentVersion] = useState(0);
  const [imageVersion, setImageVersion] = useState(0);
  const imageKey = useMemo(() => {
    return `image-${articleId || "new"}-v${imageVersion}`;
  }, [articleId, imageVersion]);
  // track if user is typing, so we don't overwrite their content
  const userDirtyRef = useRef(false);

  // priview in app
  const [isPreviewOpen, setPreviewOpen] = useState(false);
  const openPreview = useCallback(() => setPreviewOpen(true), []);
  const closePreview = useCallback(() => setPreviewOpen(false), []);
  const canPreviewInApp = !!articleId && !isExternal;

  // changed
  const [isChanged, setChanged] = useState(false);
  const [isDiscardModalOpen, setDiscardModalOpen] = useState(false);
  const openDiscardModal = () => setDiscardModalOpen(true);
  const closeDiscardModal = () => setDiscardModalOpen(false);

  // diff
  const [isOpenChangeLogsModal, setOpenChangeLogsModal] = useState(false);
  const [diffLoading, setDiffLoading] = useState(false);
  const [activeVersion, setActiveVersion] = useState(null);
  const [pendingVersion, setPendingVersion] = useState(null);

  const handleImportFile = useCallback(
    async (file) => {
      if (!articleId) return;

      setIsImporting(true);
      console.log('isImporting', isImporting)

      try {
        await ApiArticles.importFile(articleId, file);

        // The import has stored the file's cover on S3 by the time it answers;
        // the image field below shows it from there.
        let resp = await ApiArticles.getById(articleId);
        let full = resp?.data?.page || resp?.data?.article || resp?.data || null;

        // refresh signed urls in body (images/videos)
        if (full?.text) {
          try {
            full = { ...full, text: await fetchUpdatedMediaUrls(full.text) };
          } catch (e) {}
        }

        setFetchedArticle(full);
        // A cover picked or removed before the import must not ride the next
        // autosave: it would replace or clear the imported one. null = unchanged.
        form?.setFieldsValue?.({ ...full, image: null });

        setIsExternal(!!full?.external);

        setArticleImage(null);
        setImageVersion?.((v) => v + 1);

        const raw = normalizeMediaBlocks(full?.text ?? "");
        setEditorContent(raw);
        userDirtyRef.current = false;

        openNotification?.({
          type: "success",
          message: <p>Imported successfully</p>,
        });

        setChanged(full?.status === "pending");
      } catch (e) {
        console.error("❌ Import failed:", e);

        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.error?.message ||
          e?.message ||
          "Import failed. Please try another file.";

        openNotification?.({
          type: "error",
          message: <p>{msg}</p>,
        });
      } finally {
        setContentVersion((v) => v + 1);
        setIsImporting(false);

        // exit edit mode after import
        setEditMode(false);
        navigate(`${urlPart}/${articleId}`);
      }
    },
    [
      articleId,
      form,
      openNotification,
      fetchUpdatedMediaUrls,
      navigate,
      urlPart,
      setImageVersion,
    ]
  );
  // ---- choose data source for rendering ----
  // const viewData = fetchedArticle || data;
  const viewData = useMemo(() => {
    const base = (() => {
      if (!fetchedArticle) return data;
      if (!data) return fetchedArticle;

      if (String(fetchedArticle.id) !== String(data.id)) return fetchedArticle;

      const fu = new Date(fetchedArticle.updatedAt || 0).getTime();
      const du = new Date(data.updatedAt || 0).getTime();

      return du >= fu ? data : fetchedArticle;
    })();

    if (base && !base.associations && fetchedArticle?.associations) {
      return { ...base, associations: fetchedArticle.associations };
    }

    return base;
  }, [fetchedArticle, data]);

  const diffFields = useMemo(
    () => [
      { key: "title", label: "Title", type: "string" },
      {
        key: "categoryId",
        label: "Category",
        type: "entity",
        resolve: (id) => categories.find((c) => c.id === id)?.name || "—",
        getId: (id) => id,
      },
      {
        key: "tags",
        label: "Tags",
        type: "array-entity",
        resolveMany: (vals) =>
          (vals || []).map((v) => {
            if (typeof v === "object") return v.name ?? v.title ?? v.id;
            const tag = tags.find((t) => t.id === v);
            return tag?.name ?? String(v);
          }),
        getId: (v) => (typeof v === "object" ? v.id : v),
      },
      {
        key: "userId",
        label: "Author",
        type: "entity",
        resolve: (id) => authors.find((a) => a.id === id)?.fullName || "—",
        getId: (id) => id,
      },
      { key: "external", label: "External Article", type: "boolean" },
      { key: "link", label: "Source URL", type: "url" },
      { key: "image", label: "", type: "image" },
      { key: "text", label: "", type: "text" },
    ],
    [authors, categories, tags]
  );

  // ---- edit mode ----
  useEffect(() => {
    if (mode === "edit" || mode === "create") setEditMode(true);
    if (mode === "show") setEditMode(false);
  }, [mode]);

  // ---- NEW: keep pendingVersion in sync for diff modal default (use full if exists) ----
  useEffect(() => {
    setPendingVersion(fetchedArticle || data || null);
  }, [data, fetchedArticle]);

  // ---- changed flag: pending -> changed (use full if exists) ----
  useEffect(() => {
    const st = (fetchedArticle || data)?.status;
    if (st === "pending") setChanged(true);
  }, [data?.status, fetchedArticle?.status]);

  // ---- NEW: load full article by id whenever drawer opens / articleId changes ----
  useEffect(() => {
    if (!articleId) {
      setFetchedArticle(null);
      setEditorContent(null);
      userDirtyRef.current = false;
      return;
    }

    let cancelled = false;

    (async () => {
      setIsLoadingArticle(true);
      try {
        const resp = await ApiArticles.getById(articleId);
        const full =
          resp?.data?.page || resp?.data?.article || resp?.data || null;

        if (cancelled) return;

        setFetchedArticle(full);
        form?.setFieldsValue?.({ ...full });

        if (full?.image) form?.setFieldValue?.("image", full.image);
        setImageVersion((v) => v + 1);

        setIsExternal(!!full?.external);
        setArticleImage(full?.image);

        const raw = normalizeMediaBlocks(full?.text ?? "");
        setEditorContent(raw);
        userDirtyRef.current = false;

        setContentVersion((v) => v + 1);

        setChanged(full?.status === "pending");
      } catch (e) {
        console.error("❌ Failed to load article by id:", e);
      } finally {
        if (!cancelled) setIsLoadingArticle(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [articleId, form]);

  const debouncedAutoSave = useCallback(
    debounce((fields) => {
      if (articleId) onSaveSilent?.(articleId, fields);
    }, 500),
    [articleId, onSaveSilent]
  );

  const handleFieldChange = useCallback(
    (field, value) => {
      form.setFieldValue(field, value);
      const fields = form.getFieldsValue();
      debouncedAutoSave({ ...fields, [field]: value });
      setChanged(true);

      setFetchedArticle((prev) => (prev ? { ...prev, [field]: value } : prev));
    },
    [form, debouncedAutoSave]
  );

  // ---- handlers ----
  const closeHandler = useCallback(() => {
    navigate(urlPart);
    setEditMode(false);
    onClose?.();
  }, [navigate, urlPart, onClose]);

  const editHandler = useCallback(() => {
    if (!articleId) return;
    navigate(`${urlPart}/${articleId}/edit`);
    setEditMode(true);
  }, [navigate, urlPart, articleId]);

  const handleDiscard = useCallback(async () => {
    try {
      if (!articleId) return;
      await onDiscard?.(articleId);

      userDirtyRef.current = false;
      setChanged(false);

      const resp = await ApiArticles.getById(articleId);
      const full =
        resp?.data?.page || resp?.data?.article || resp?.data || null;

      setFetchedArticle(full);
      form?.setFieldsValue?.({ ...full });

      setIsExternal(!!full?.external);
      setArticleImage(full?.image);
      setEditorContent(normalizeMediaBlocks(full?.text ?? ""));
      setContentVersion((v) => v + 1);
    } catch (error) {
      console.error("Discard error:", error);
    }
  }, [onDiscard, articleId, form]);

  const handlePublish = useCallback(async () => {
    try {
      if (!articleId) return;
      await onPublish?.(articleId);
      setChanged(false);
      // Allow the view-mode effect to refresh content/media URLs again.
      userDirtyRef.current = false;
      navigate(`/content/pages/${articleId}`);
      setEditMode(false);
      onClose?.();
    } catch (error) {
      console.error("Publish error:", error);
    }
  }, [onPublish, articleId, navigate, onClose]);

  const cancelHandler = useCallback(() => {
    if (articleId) navigate(`${urlPart}/${articleId}`);
    else navigate(`${urlPart}`);

    form?.resetFields?.();
    setEditMode(false);
    // Allow the view-mode effect to refresh content/media URLs again.
    userDirtyRef.current = false;

    if (viewData?.status === "draft") onClose?.();
  }, [navigate, urlPart, articleId, form, viewData?.status, onClose]);

  const onHandleContentUpdate = useCallback(
    (value) => {
      userDirtyRef.current = true; // user started typing
      const normalized = normalizeMediaBlocks(value);
      setEditorContent(normalized);
      handleFieldChange("text", normalized);
    },
    [handleFieldChange]
  );

  const onHandleUpdateImage = useCallback(
    (image) => {
      setArticleImage(image);
      handleFieldChange("image", image);
    },
    [handleFieldChange]
  );

  const updateArticleTags = useCallback(
    (values) => handleFieldChange("tags", values),
    [handleFieldChange]
  );

  const updateCategory = useCallback(
    (category) => handleFieldChange("categoryId", category),
    [handleFieldChange]
  );

  const updateImageStatus = useCallback(
    (value) => updateHasImage?.(value),
    [updateHasImage]
  );

  const updateAuthor = useCallback(
    (author) => {
      const authorIdResolved = authors.find((a) => a.fullName === author)?.id;
      handleFieldChange("userId", authorIdResolved);
    },
    [authors, handleFieldChange]
  );

  const updateIsExternal = useCallback(
    (value) => {
      setIsExternal(value);
      handleFieldChange("external", value);
    },
    [handleFieldChange]
  );

  useEffect(() => {
    const baseText = viewData?.text;
    if (!articleId || !baseText) return;

    // While the user is editing, NEVER touch the editor content from here.
    // This effect re-runs on every keystroke (handleFieldChange updates
    // fetchedArticle.text -> viewData.text) and on the autosave echo coming
    // back through SSE/list refresh. Bumping contentVersion remounts the
    // MDXEditor (editorKey), which used to teleport the cursor to the end of
    // the section and swallow keystrokes typed during the remount.
    if (editMode || userDirtyRef.current) return;

    const raw = normalizeMediaBlocks(baseText ?? "");
    setEditorContent(raw);

    if (!raw.trim()) {
      setContentVersion((v) => v + 1);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const updated = await fetchUpdatedMediaUrls(raw);
        if (cancelled) return;

        if (editMode) return;
        if (userDirtyRef.current) return;

        setEditorContent(updated ?? raw);
        setContentVersion((v) => v + 1);
      } catch (e) {
        // Same guards as the success path: a failed media-URL refresh must
        // not remount the editor mid-typing.
        if (!cancelled && !userDirtyRef.current) {
          setEditorContent(raw);
          setContentVersion((v) => v + 1);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [articleId, viewData?.text, editMode, fetchUpdatedMediaUrls]);

  const editorKey = useMemo(() => {
    return `editor-${articleId || "new"}-v${contentVersion}`;
  }, [articleId, contentVersion]);

  // -------- misc helpers --------
  const delay = (ms) => new Promise((res) => setTimeout(res, ms));

  const fetchIconWithDelay = useCallback(async (...args) => {
    await delay(0);
    try {
      let res = await ApiArticles.fetchIcon(...args);
      await delay(5000);
      res = await ApiArticles.fetchIcon(...args);
      return res;
    } catch (error) {
      console.error("❌ fetchIcon failed", error);
      throw error;
    }
  }, []);

  const buildPendingSnapshot = useCallback(() => {
    const base = viewData || {};
    const formVals =
      typeof form?.getFieldsValue === "function" ? form.getFieldsValue() : {};
    return {
      ...base,
      ...formVals,
      id: base?.id || articleId,
      text:
        typeof editorContent === "string"
          ? editorContent
          : formVals.text ?? base?.text ?? "",
      image: articleImage ?? formVals.image ?? base?.image,
      external:
        typeof formVals.external === "boolean"
          ? formVals.external
          : !!isExternal,
      link: formVals.link ?? base?.link ?? "",
      tags: formVals.tags ?? base?.tags ?? [],
      categoryId: formVals.categoryId ?? base?.categoryId ?? null,
      userId: formVals.userId ?? base?.userId ?? null,
      title: formVals.title ?? base?.title ?? "",
      statusSummary: base?.statusSummary,
    };
  }, [form, viewData, editorContent, articleImage, isExternal, articleId]);

  const openChangeLogsModal = useCallback(async () => {
    if (!articleId) {
      setPendingVersion(buildPendingSnapshot());
      setActiveVersion(null);
      setOpenChangeLogsModal(true);
      return;
    }

    setDiffLoading(true);
    try {
      const [pendingResp, activeResp] = await Promise.all([
        ApiArticles.getById(articleId),
        ApiArticles.getById(articleId, { status: "active" }),
      ]);

      let pending =
        pendingResp?.data?.page ||
        pendingResp?.data?.article ||
        pendingResp?.data ||
        null;

      let active =
        activeResp?.data?.page ||
        activeResp?.data?.article ||
        activeResp?.data ||
        null;

      if (pending?.text) {
        pending = { ...pending, text: await fetchUpdatedMediaUrls(pending.text) };
      }
      if (active?.text) {
        active = { ...active, text: await fetchUpdatedMediaUrls(active.text) };
      }

      setPendingVersion(pending);
      setActiveVersion(active);
    } catch (err) {
      console.error("Error fetching article versions for diff:", err);
      setPendingVersion(buildPendingSnapshot());
      setActiveVersion(null);
    } finally {
      setDiffLoading(false);
      setOpenChangeLogsModal(true);
    }
  }, [articleId, buildPendingSnapshot, fetchUpdatedMediaUrls]);

  const handleChangeLogsCancel = useCallback(() => {
    setOpenChangeLogsModal(false);
  }, []);

  const associationGroups = useMemo(() => {
    const src = viewData?.associations;

    if (!src) return [];

    // associations: [{ key, title, count?, items: [{ id, label, href?, icon? }] }]
    if (Array.isArray(src)) {
      return src
        .filter(Boolean)
        .map((g) => ({
          key: g.key || g.title,
          title: g.title,
          count: Number.isInteger(g.count) ? g.count : undefined,
          items: Array.isArray(g.items)
            ? g.items.filter(Boolean).map((it) => ({
                id: it.id,
                label: it.label,
                href: it.href,
                onClick: it.onClick,
                icon: it.icon,
              }))
            : [],
        }))
        .filter((g) => g.title);
    }

    // associations: { tasks: {...}, scenarios: {...} }
    if (typeof src === "object") {
      return Object.entries(src)
        .map(([key, g]) => ({
          key,
          title: g?.title || key,
          count: Number.isInteger(g?.count) ? g.count : undefined,
          items: Array.isArray(g?.items)
            ? g.items.map((it) => ({
                id: it.id,
                label: it.label,
                href: it.href,
                icon: it.icon,
              }))
            : [],
        }))
        .filter((g) => g.title);
    }

    return [];
  }, [viewData]);
  // -------- render --------
  const drawerContent = useMemo(() => {
    console.log('editorContent',editorContent)
    if (!articleId) return null;

    const d = viewData;
    if (!d || isLoadingArticle) {
      return articleSpinner;
    }

    return (
      <div style={{ position: "relative" }}>
        {isImporting ? importSpinnerOverlay : null}

        <Form form={form}>
          <DrawerHeader
            onImportFile={handleImportFile}
            onPreviewInApp={canPreviewInApp ? openPreview : null}
            title={
              <Form.Item name={"title"} initialValue={d.title}>
                <FormHeaderTextarea
                  data-testid="article-title"
                  data-ready={isLoading || isLoadingArticle ? "false" : "true"}
                  readonly={!editMode || isImporting}
                  placeholder={"Title"}
                  onChange={(e) => handleFieldChange("title", e.target.value)}
                />
              </Form.Item>
            }
            onEdit={editHandler}
            onDiscard={openDiscardModal}
            onPublish={handlePublish}
            onCancel={cancelHandler}
            onViewChanges={openChangeLogsModal}
            onClose={closeHandler}
            isEditing={editMode}
            isLoading={isLoading || isImporting}
            isChanged={isChanged}
            onToggleActive={() => toggleActive?.(d)}
            isActive={!!d?.isActive}
            onDelete={() => remove?.(d?.id, d?.title || d?.name || d?.text)}
            onDuplicate={() => duplicate?.(d)}
            resourceTitle={d?.title || d?.text}
          />

          <Form.Item>
            <FormFieldName text="Status:" />
            <div>
              <UserActivityStatus userStatus={d.statusSummary} />
            </div>
          </Form.Item>

          <Form.Item name={"categoryId"} initialValue={d.categories}>
            <div className="article-modal-tags-label-wrapper">
              <span className="article-modal-tags-label">Category: </span>
            </div>
            <FormSelect
              options={categories.map((c) => ({
                value: c.id,
                label: c.name,
                key: c.id,
              }))}
              defaultValue={categories
                .map((cat) => ({ value: cat.id, key: cat.id, label: cat.name }))
                .find((c) => c.value === d.categoryId)}
              onSelect={updateCategory}
              placeholder={editMode && !isImporting ? "Select category..." : ""}
              style={{ minWidth: 160 }}
              readonly={!editMode || isImporting}
              getPopupContainer={(trigger) => trigger.parentNode}
            />
          </Form.Item>

          <Form.Item name={"tags"} initialValue={d.tags}>
            <div className="article-modal-tags-label-wrapper">
              <span className="article-modal-tags-label">Tags: </span>
            </div>
            <div style={{ paddingLeft: 10 }}>
              <TagsSelect
                options={tags}
                defaultValue={d.tags}
                onChange={updateArticleTags}
                onCreate={updateArticleTags}
                readonly={!editMode || isImporting}
                placeholder="+ Add Tag"
                isEditing={true}
              />
            </div>
          </Form.Item>

          <Form.Item
            name={"userId"}
            initialValue={authors.find((a) => a.id === d.userId)?.id}
          >
            <div className="article-modal-tags-label-wrapper">
              <span className="article-modal-tags-label">Author: </span>
            </div>
            <FormSelect
              placeholder={editMode && !isImporting ? "Select author" : ""}
              options={authors?.map((author) => ({
                id: author.id,
                value: author.fullName,
              }))}
              defaultValue={() => {
                const usr = authors.find((a) => a.id === d.userId) || currentUser;
                return usr ? { id: usr?.id, value: usr?.fullName } : null;
              }}
              onSelect={updateAuthor}
              readonly={!editMode || isImporting}
              getPopupContainer={(trigger) => trigger.parentNode}
            />
          </Form.Item>

          <Form.Item name={"external"} initialValue={isExternal}>
            <div className="article-modal-tags-label-wrapper">
              <span className="article-modal-tags-label">External Article: </span>
            </div>
            <FormSwitch
              readOnly={!editMode || isImporting}
              checked={isExternal}
              onChange={updateIsExternal}
            />
          </Form.Item>

          {isExternal && (
            <Form.Item name={"link"} initialValue={d.link}>
              <div className="article-modal-tags-label-wrapper">
                <span className="article-modal-tags-label">Source URL: </span>
              </div>
              <FormInput
                defaultValue={d.link}
                readonly={!editMode || isImporting}
                placeholder={editMode && !isImporting ? "Source URL" : ""}
                onChange={(e) => handleFieldChange("link", e.target.value)}
              />
            </Form.Item>
          )}

          <Form.Item name={"image"} initialValue={articleImage}>
            <FormImage
              key={imageKey}
              readonly={!editMode || isImporting}
              onChange={onHandleUpdateImage}
              articleId={articleId}
              updateImageStatus={updateImageStatus}
              updateIsUpdatedImage={updateIsUpdatedImage}
              fetchIconApi={fetchIconWithDelay}
              form={form}
              fetchFromFormKeyAutorized="external"
              fetchFromFormKeyUrl="link"
            />
          </Form.Item>

          {!isExternal ? (
            <Form.Item name={"text"}>
              {editorContent === null ? (
                <div style={{ padding: 12, opacity: 0.7 }}>Loading…</div>
              ) : editMode ? (
                <Editor
                  key={editorKey}
                  content={editorContent}
                  readonly={isImporting}
                  onChange={onHandleContentUpdate}
                  articleId={articleId}
                />
              ) : (
                <Editor
                  key={editorKey}
                  content={normalizeMediaBlocks(editorContent)}
                  readonly
                />
              )}
            </Form.Item>
          ) : null}

          {associationGroups?.length ? (
            <AssociationsBlock groups={associationGroups} />
          ) : null}
        </Form>
      </div>
    );
  }, [
    articleId,
    viewData,
    associationGroups,
    isLoadingArticle,
    form,
    editMode,
    isLoading,
    isChanged,
    isExternal,
    editorContent,
    editorKey,
    articleImage,
    categories,
    tags,
    authors,
    currentUser,
    canPreviewInApp,
    handleFieldChange,
    editHandler,
    openDiscardModal,
    handlePublish,
    cancelHandler,
    openChangeLogsModal,
    closeHandler,
    toggleActive,
    remove,

    updateCategory,
    updateArticleTags,
    updateAuthor,
    updateIsExternal,
    onHandleUpdateImage,
    updateImageStatus,
    updateIsUpdatedImage,
    fetchIconWithDelay,
  ]);

  return (
    <>
      <Drawer open={!!articleId} onClose={closeHandler} destroyOnHidden={true}>
        {drawerContent}
      </Drawer>

      <DiscardModal
        open={isDiscardModalOpen}
        onConfirm={async () => {
          await handleDiscard();
          closeDiscardModal();
          onClose?.();
        }}
        onCancel={closeDiscardModal}
      />

      <ChangesDiffModalComponent
        key={`diff-${articleId || "new"}`}
        isModalOpen={isOpenChangeLogsModal}
        handleCancel={handleChangeLogsCancel}
        title={"Changes"}
        loading={diffLoading}
        pending={pendingVersion}
        active={activeVersion}
        fields={diffFields}
        titleFieldKey="title"
      />
      <Modal
        title="Preview in app"
        open={isPreviewOpen}
        onCancel={closePreview}
        footer={null}
        destroyOnHidden
      >
        <ScenarioSimulatorQr
          itemType="article"
          scenarioVersionId={pendingVersion?.id || viewData?.id || articleId}
          scenarioId={articleId}
        />
      </Modal>
    </>
  );
};